<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from '#imports'
import { useI18n } from 'vue-i18n'
import { useAuthUser } from '~/composables/useAuthUser'
import { useUserProfile } from '~/composables/useUserProfile'

/**
 * Account menu in the header: a round black avatar with the user's initials
 * and a chevron, opening a small raised card with the profile, premium,
 * feature-request and logout entries.
 */
const { t } = useI18n()
const { logout } = useAuthUser()
const { profile, loadProfile } = useUserProfile()
const menuOpen = ref(false)
const featureRequestDialogOpen = ref(false)

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value
}

const handleLogout = async () => {
  await logout()
  menuOpen.value = false
}

const closeMenu = () => {
  menuOpen.value = false
}

const openFeatureRequest = () => {
  featureRequestDialogOpen.value = true
  menuOpen.value = false
}

// Get user initials for avatar
const userInitials = computed(() => {
  if (profile.value?.full_name) {
    const names = profile.value.full_name.split(' ').filter(n => n.length > 0)
    if (names.length >= 2) {
      const firstName = names[0]
      const lastName = names[names.length - 1]
      if (firstName && firstName.length > 0 && lastName && lastName.length > 0) {
        return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
      }
    }
    const firstNameOnly = names[0]
    if (names.length > 0 && firstNameOnly && firstNameOnly.length > 0) {
      return firstNameOnly.charAt(0).toUpperCase()
    }
  }
  return '?'
})

// Close menu when clicking outside
onMounted(() => {
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement
    if (!target.closest('[data-user-menu]')) {
      menuOpen.value = false
    }
  }
  document.addEventListener('click', handleClickOutside)

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })
})

loadProfile()

/** Shared look of every dropdown row; the logout row overrides the colours. */
const itemClass = 'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-body transition-colors duration-300 hover:bg-surface hover:text-ink dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
</script>

<template>
  <div class="relative" data-user-menu>
    <!-- Avatar trigger -->
    <button
      type="button"
      class="flex items-center gap-2 rounded-full p-1 pr-2 transition-colors duration-300 hover:bg-surface dark:hover:bg-gray-800"
      :aria-label="t('navbar.openUserMenu')"
      :aria-expanded="menuOpen"
      aria-haspopup="menu"
      @click.stop="toggleMenu"
    >
      <!-- `!text-white` / `dark:!text-ink`: the global span rule would otherwise paint the initials dark -->
      <span class="inline-flex size-9 items-center justify-center rounded-full bg-ink font-title text-xs font-bold !text-white dark:bg-white dark:!text-ink">
        {{ userInitials }}
      </span>
      <i
        class="bi bi-chevron-down text-xs text-ink transition-transform duration-300 dark:text-white"
        :class="{ 'rotate-180': menuOpen }"
        aria-hidden="true"
      />
    </button>

    <!-- Dropdown -->
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="-translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="-translate-y-1 opacity-0"
    >
      <div
        v-if="menuOpen"
        class="absolute right-0 top-full z-50 mt-3 w-64 overflow-hidden rounded-[0.625rem] border border-line bg-white shadow-lift dark:border-gray-700 dark:bg-gray-800"
        role="menu"
      >
        <!-- Who is signed in -->
        <div class="border-b border-line px-4 py-3 dark:border-gray-700">
          <div class="truncate font-title text-base font-semibold text-ink dark:text-white">
            {{ profile?.full_name || t('navbar.user') }}
          </div>
          <div class="mt-0.5 text-xs text-muted dark:text-gray-400">
            {{ t('navbar.signedIn') }}
          </div>
        </div>

        <div class="p-2">
          <LocaleLink to="/profile" :class="itemClass" role="menuitem" @click="closeMenu">
            <i class="bi bi-person text-lg" aria-hidden="true" />
            {{ t('navbar.profile') }}
          </LocaleLink>

          <LocaleLink to="/admin/premium" :class="itemClass" role="menuitem" @click="closeMenu">
            <i class="bi bi-star-fill text-lg text-yellow-400" aria-hidden="true" />
            {{ t('navbar.premium') }}
          </LocaleLink>

          <LocaleLink to="/affiliate" :class="itemClass" role="menuitem" @click="closeMenu">
            <i class="bi bi-cash-coin text-lg text-green-600 dark:text-green-400" aria-hidden="true" />
            {{ t('navbar.affiliate') }}
          </LocaleLink>

          <button type="button" :class="itemClass" role="menuitem" @click="openFeatureRequest">
            <i class="bi bi-lightbulb text-lg" aria-hidden="true" />
            {{ t('navbar.featureRequest') }}
          </button>

          <div class="px-3 py-2">
            <div class="mb-1.5 text-xs font-medium text-muted dark:text-gray-400">{{ t('navbar.language') }}</div>
            <LanguageSwitcher grid />
          </div>

          <button
            type="button"
            class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-red-600 transition-colors duration-300 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            role="menuitem"
            @click="handleLogout"
          >
            <i class="bi bi-box-arrow-right text-lg" aria-hidden="true" />
            {{ t('navbar.logout') }}
          </button>
        </div>
      </div>
    </Transition>

    <!-- Feature Request Dialog -->
    <CommonFeatureRequestDialog v-model="featureRequestDialogOpen" />
  </div>
</template>
